import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Biolink} from '../biolink';
import {BiolinkItemConfig, BiolinksService} from '../biolinks.service';
import { finalize, tap } from 'rxjs/operators';
import {Toast} from '@common/core/ui/toast.service';
import {FormBuilder} from '@angular/forms';
import {BiolinkWidgetsService} from './widgets/biolink-widgets.service';
import {Link} from '../../../shared/link/link';
import {
    CrupdateLinkModalComponent,
    CrupdateLinkModalData,
} from '../../link/crupdate-link-modal/crupdate-link-modal.component';
import {Modal} from '@common/core/ui/dialogs/modal.service';
import {BiolinkWidget} from './widgets/biolink-widget';
import {ConfirmModalComponent} from '@common/core/ui/confirm-modal/confirm-modal.component';
import {BiolinkLink} from './biolink-link';

export const BiolinkEditorTabs = {
    links: 0,
    appearance: 1,
    settings: 2,
};

@Injectable({
    providedIn: 'root',
})
export class BiolinkEditorService {
    biolink$ = new BehaviorSubject<Biolink>(null);
    loading$ = new BehaviorSubject<boolean>(false);
    activeTab$ = new BehaviorSubject<number>(BiolinkEditorTabs.links);

    appearanceForm = this.fb.group({
        fontConfig: {},
        btnConfig: this.fb.group({
            color: this.fb.control(''),
            variant: this.fb.control(''),
            type: this.fb.control(''),
        }),
        bgConfig: {},
    });

    constructor(
        public biolinks: BiolinksService,
        private toast: Toast,
        private fb: FormBuilder,
        private widgets: BiolinkWidgetsService,
        private modal: Modal
    ) {}

    saveAppearance() {
        this.loading$.next(true);
        return this.biolinks
            .saveAppearance(this.biolink$.value.id, this.appearanceForm.value)
            .pipe(finalize(() => this.loading$.next(false)))
            .subscribe(() => {
                this.toast.open('Appearance saved');
            });
    }

    reloadBiolink(biolinkId?: number) {
        biolinkId = biolinkId || this.biolink$.value.id;
        this.loading$.next(true);
        this.biolinks
            .get(biolinkId, {with: ['appearance', 'rules', 'pixels', 'domain']})
            .pipe(finalize(() => this.loading$.next(false)))
            .subscribe(response => {
                this.biolink$.next(response.biolink);
                this.appearanceForm.patchValue(
                    response.biolink.appearance?.config
                );
            });
    }

    crupdateWidget(widgetId: number, type: string, config: object) {
        this.loading$.next(true);
        const request = widgetId
            ? this.widgets.update(this.biolink$.value.id, widgetId, config)
            : this.widgets.create(
                  this.biolink$.value.id,
                  type,
                  this.biolink$.value.content.length + 1,
                  config
              );
        return request.pipe(
            finalize(() => this.loading$.next(false)),
            tap(() =>  this.reloadBiolink()),
        );
    }

    openCrupdateLinkDialog(link?: Link, config: CrupdateLinkModalData = {}) {
        const position = this.biolink$.value.content.length + 1;
        const pivotData = link ? {} : {position};
        const baseConfig: CrupdateLinkModalData = {
            groups: {[this.biolink$.value.id]: pivotData},
            link,
            featuresToHide: {
                alias: true,
                domain: true,
                linkGroups: true,
                tags: true,
            },
        };
        this.modal
            .open(CrupdateLinkModalComponent, {...baseConfig, ...config})
            .afterClosed()
            .subscribe(newLink => {
                if (newLink) {
                    this.reloadBiolink();
                }
            });
    }

    maybeDeleteContentItem(item: BiolinkLink | BiolinkWidget) {
        this.modal
            .open(ConfirmModalComponent, {
                title: 'Delete Widget',
                body: `Are you sure you want to delete this ${item.model_type}?`,
                ok: 'Delete',
            })
            .afterClosed()
            .subscribe(confirmed => {
                if (!confirmed) return;
                this.loading$.next(true);
                this.biolinks
                    .detach(this.biolink$.value.id, item)
                    .pipe(finalize(() => this.loading$.next(false)))
                    .subscribe(() => {
                        this.reloadBiolink(this.biolink$.value.id);
                    });
            });
    }

    updateContentItemConfig(
        item: Link | BiolinkWidget,
        config: BiolinkItemConfig
    ) {
        this.loading$.next(true);
        this.biolinks
            .updateContentItemConfig(this.biolink$.value.id, item, config)
            .pipe(finalize(() => this.loading$.next(false)))
            .subscribe(() => {
                this.reloadBiolink();
            });
    }
}
